/**
 * ============================================================================
 * PARTS STORE ACTIONS
 * Refactored using generic Apollo helpers and parts-specific business logic
 * ============================================================================
 */

import * as ACTION_TYPES from './action-types'
import * as PART_MUTATIONS from './mutation-types'
import * as TABLE_MUTATIONS from '@/store/modules/Table/mutation-types'

// Models
import { Part } from '@/models/Part'
import { GroupedSale } from '@/models/GroupedSale'
import { createPartCategorySet } from '@/models/CategorySet'
import { createTpParameters } from '@/models/Part/TpParameters'
import { pickSaleOutlook } from '@/utils/saleOutlook'

// Generic Apollo helpers
import {
	executeSimpleQuery,
	executeMutation,
	executeQueryWithLoading
} from '@/store/helpers/apollo'

// Parts-specific helpers
import {
	buildPartsQueryPayload,
	buildPartsSort,
	buildPartsColumns,
	processNearestChildren,
	cleanRelatedPartsPayload
} from '@/store/helpers/parts'

// GraphQL imports
import FetchRelatedParts from './Schema/Queries/FetchRelatedParts.gql'
import FetchParts from './Schema/Queries/FetchParts.gql'
import FetchModels from './Schema/Queries/FetchModels.gql'
import FetchTpParams from './Schema/Queries/FetchTpParams.gql'
import SearchPartCategoriesQuery from './Schema/Queries/SearchPartCategories.gql'
import FetchPartSearchSuggestions from './Schema/Queries/FetchPartSearchSuggestions.gql'
import FetchInputPartSearchSuggestion from './Schema/Queries/FetchInputPartSearchSuggestion.gql'
import FetchPartCategories from './Schema/Queries/FetchPartCategories.gql'
import FetchModifications from './Schema/Queries/FetchModifications.gql'
import FetchEngines from './Schema/Queries/FetchEngines.gql'
import CreatePart from './Schema/Mutations/CreatePart.gql'
import FetchPart from './Schema/Queries/FetchPart.gql'
import DeletePart from './Schema/Mutations/DeletePart.gql'
import FetchParent from './Schema/Queries/FetchParent.gql'
import FetchPartTpParameters from './Schema/Queries/FetchPartTpParameters.gql'
import FetchGroupedSales from './Schema/Queries/FetchGroupedSales.gql'
import EditPartMutation from './Schema/Mutations/EditPart.gql'
import HandoverOrderParts from './Schema/Mutations/HandoverOrderParts.gql'
import FetchCategorySet from './Schema/Queries/FetchCategorySet.gql'
import DeletePartsMutation from './Schema/Mutations/DeleteParts.gql'
import FetchExternalItems from './Schema/Queries/FetchExternalItems.gql'
import DetachParts from './Schema/Mutations/DetachParts.gql'
import RestorePartMutation from './Schema/Mutations/RestorePart.gql'
import FetchPartLogs from './Schema/Queries/FetchPartLogs.gql'
import FetchPartParent from './Schema/Queries/FetchPartParent.gql'
import FetchPartTasks from './Schema/Queries/FetchPartTasks.gql'
import FetchPartsMeta from './Schema/Queries/FetchPartsMeta.gql'
import FetchParentGroupedSale from './Schema/Queries/FetchParentGroupedSale.gql'
import FetchPartCategorySearchSuggestions from './Schema/Queries/FetchPartCategorySearchSuggestions.gql'
import BulkUpdatePartsPrice from './Schema/Mutations/BulkUpdatePartsPrice.gql'
import BulkUpdatePartsLocation from './Schema/Mutations/BulkUpdatePartsLocation.gql'
import FetchSuspensionTypes from './Schema/Queries/FetchSuspensionTypes.gql'
import RefreshPartSuggestedLocation from './Schema/Mutations/RefreshPartSuggestedLocation.gql'
import FetchPartLocations from './Schema/Queries/FetchPartLocations.gql'
import FetchPartAiSuggestedPrice from './Schema/Queries/FetchPartAiSuggestedPrice.gql'
import FetchPartSaleOutlook from './Schema/Queries/FetchPartSaleOutlook.gql'
import FetchPartWheelSpecifications from './Schema/Queries/FetchPartWheelSpecifications.gql'
import FetchPartCategoryKinds from './Schema/Queries/FetchPartCategoryKinds.gql'
import FetchWheelSpecifications from './Schema/Queries/FetchWheelSpecifications.gql'
import EditWheel from './Schema/Mutations/EditWheel.gql'
import FetchPriceCheckerStatistics from './Schema/Queries/FetchPriceCheckerStatistics.gql'
import FetchPriceCheckerCurrentAds from './Schema/Queries/FetchPriceCheckerCurrentAds.gql'
import FetchPriceCheckerChannelNames from './Schema/Queries/FetchPriceCheckerChannelNames.gql'
import FetchRelatedFitments from './Schema/Queries/FetchRelatedFitments.gql'
import FetchPartsStatistics from './Schema/Queries/FetchPartsStatistics.gql'
import FetchPartsDrafts from './Schema/Queries/FetchPartsDrafts.gql'
import FetchPartsDraftsMeta from './Schema/Queries/FetchPartsDraftsMeta.gql'
import ExportParts from './Schema/Mutations/ExportParts.gql'
import GetVehicleFromPlateNumber from './Schema/Queries/GetVehicleFromPlateNumber.gql'

// In-flight deduplication for per-department vehicle specification fetches
const vehicleSpecificationsInFlight = new Map()

export default {
	/**
     * ========================================================================
     * PART LOCATIONS & SUGGESTIONS
     * ========================================================================
     */

	/**
     * Fetch part locations
     */
	async [ACTION_TYPES.FETCH_PART_LOCATIONS] ({ commit, state }, partId) {
		const data = await executeSimpleQuery({
			query: FetchPartLocations,
			variables: { id: partId }
		})

		const part = new Part({
			...state.currentPart,
			...data.part
		})
		commit(PART_MUTATIONS.SET_CURRENT_PART, part)
		return data.part.location
	},

	/**
     * Refresh suggested location
     */
	async [ACTION_TYPES.REFRESH_SUGGESTED_LOCATION] ({ commit, state, dispatch }, payload) {
		return executeMutation({
			mutation: {
				mutation: RefreshPartSuggestedLocation,
				variables: { payload }
			},
			onSuccess: (data) => {
				const part = new Part({
					...state.currentPart,
					...data.refreshPartSuggestedLocation
				})
				commit(PART_MUTATIONS.SET_CURRENT_PART, part)
				dispatch(ACTION_TYPES.FETCH_PART_TASKS, payload.id)
				return data.refreshPartSuggestedLocation
			}
		})
	},

	/**
     * ========================================================================
     * PRICE CHECKER
     * ========================================================================
     */

	/**
     * Fetch part AI suggested price
     */
	async [ACTION_TYPES.FETCH_PART_AI_SUGGESTED_PRICE] (_, id) {
		const data = await executeSimpleQuery({
			query: FetchPartAiSuggestedPrice,
			variables: { id }
		})
		return data.part?.aiSuggestedPrice ?? null
	},

	/**
     * Fetch part sale probability and expected sale days
     */
	async [ACTION_TYPES.FETCH_PART_SALE_OUTLOOK] (_, id) {
		const data = await executeSimpleQuery({
			query: FetchPartSaleOutlook,
			variables: { id }
		})
		return pickSaleOutlook(data.part)
	},

	/**
     * Fetch price checker statistics
     */
	async [ACTION_TYPES.FETCH_PRICE_CHECKER_STATISTICS] (_, { oemCode, channel }) {
		try {
			const data = await executeSimpleQuery({
				query: FetchPriceCheckerStatistics,
				variables: { payload: { oemCode, channel } }
			})
			return data.priceCheckerStatistics
		} catch (_e) {
			return undefined
		}
	},

	/**
     * Fetch price checker current ads
     */
	async [ACTION_TYPES.FETCH_PRICE_CHECKER_CURRENT_ADS] (_, { oemCode, channel }) {
		try {
			const data = await executeSimpleQuery({
				query: FetchPriceCheckerCurrentAds,
				variables: { payload: { oemCode, channel } }
			})
			return data.priceCheckerCurrentAds
		} catch (_e) {
			return undefined
		}
	},

	/**
     * Fetch price checker channel names
     */
	async [ACTION_TYPES.FETCH_PRICE_CHECKER_CHANNEL_NAMES] () {
		const data = await executeSimpleQuery({
			query: FetchPriceCheckerChannelNames
		})
		return data.priceCheckerChannelNames
	},

	/**
     * ========================================================================
     * GROUPED SALES & PARENTS
     * ========================================================================
     */

	/**
     * Fetch grouped sales
     */
	async [ACTION_TYPES.FETCH_GROUPED_SALES] (_, query) {
		const data = await executeSimpleQuery({
			query: FetchGroupedSales,
			variables: {
				page: 1,
				size: 7,
				payload: { ...query }
			}
		})
		return data.groupedSales.nodes.map(item => new GroupedSale(item))
	},

	/**
     * Fetch parent
     */
	async [ACTION_TYPES.FETCH_PARENT] (_, id) {
		const data = await executeSimpleQuery({
			query: FetchParent,
			variables: { id }
		})
		return new Part(data.part)
	},

	/**
     * Fetch part parent
     */
	async [ACTION_TYPES.FETCH_PART_PARENT] ({ state, commit }, id) {
		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'partParent',
			query: {
				query: FetchPartParent,
				variables: { id }
			},
			onSuccess: (data) => {
				const part = new Part({
					...state.currentPart,
					...data.part
				})
				commit(PART_MUTATIONS.SET_CURRENT_PART, part)
				return part
			}
		})
	},

	/**
     * Fetch parent grouped sale
     */
	async [ACTION_TYPES.FETCH_PARENT_GROUPED_SALE] ({ state, commit }, id) {
		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'partParent',
			query: {
				query: FetchParentGroupedSale,
				variables: { id }
			},
			onSuccess: (data) => {
				const parentGroupedSale = new GroupedSale(data.groupedSale)
				const part = new Part({
					...state.currentPart,
					parentGroupedSale
				})
				commit(PART_MUTATIONS.SET_CURRENT_PART, part)
				return parentGroupedSale
			}
		})
	},

	/**
     * ========================================================================
     * VEHICLE SPECIFICATIONS
     * ========================================================================
     */

	/**
     * Fetch vehicle specifications (Vehicle specifications)
     */
	async [ACTION_TYPES.FETCH_VEHICLE_SPECIFICATIONS] ({ commit, state, rootState }, departmentId) {
		const vehicleType = rootState?.Auth?.user?.selectedVehicleType
		const cacheKey = `${departmentId}:${vehicleType}`

		if (departmentId && state.tpParamsByDepartment[cacheKey]) {
			commit(PART_MUTATIONS.SET_TP_PARAMS, {
				departmentId,
				vehicleType,
				data: state.tpParamsByDepartment[cacheKey]
			})
			return state.tpParamsByDepartment[cacheKey]
		}

		if (vehicleSpecificationsInFlight.has(cacheKey)) {
			return vehicleSpecificationsInFlight.get(cacheKey)
		}

		const fetchPromise = executeSimpleQuery({
			query: FetchTpParams,
			variables: {
				payload: {
					departmentId,
					enabled: true
				},
				size: 9000
			}
		}).then(data => ({
			manufacturers: data.manufacturers.nodes,
			colors: data.colors,
			bodyTypes: data.bodyTypes,
			fuelTypes: data.fuelTypes,
			fuelSystems: data.fuelSystems,
			coolingTypes: data.coolingTypes,
			driveTypes: data.driveTypes,
			gearTypes: data.gearTypes,
			steeringTypes: data.steeringTypes,
			platformTypes: data.platformTypes,
			axleConfigurations: data.axleConfigurations,
			suspensionTypes: data.suspensionTypes
		}))

		vehicleSpecificationsInFlight.set(cacheKey, fetchPromise)

		fetchPromise.then(params => {
			commit(PART_MUTATIONS.SET_TP_PARAMS, { departmentId, vehicleType, data: params })
		}).finally(() => {
			vehicleSpecificationsInFlight.delete(cacheKey)
		})

		return fetchPromise
	},

	/**
     * Fetch part vehicle specifications
     */
	async [ACTION_TYPES.FETCH_PART_VEHICLE_SPECIFICATIONS] (_, id) {
		const data = await executeSimpleQuery({
			query: FetchPartTpParameters,
			variables: { id }
		})
		return createTpParameters(data.part.vehicleSpecifications)
	},

	/**
     * Fetch models
     */
	async [ACTION_TYPES.FETCH_MODELS] ({ commit }, { id, department }) {
		const data = await executeSimpleQuery({
			query: FetchModels,
			variables: {
				payload: {
					manufacturerId: id,
					departmentId: department,
					enabled: true
				},
				size: 9000
			}
		})

		const models = data.models.nodes
		commit(PART_MUTATIONS.SET_MODELS, models)
		return models
	},

	/**
     * Fetch modifications
     */
	async [ACTION_TYPES.FETCH_MODIFICATIONS] ({ commit }, payload) {
		const data = await executeSimpleQuery({
			query: FetchModifications,
			variables: {
				payload: {
					...payload,
					enabled: true
				},
				size: 9000
			}
		})

		const modifications = data.modifications.nodes
		commit(PART_MUTATIONS.SET_MODIFICATIONS, modifications)
		return modifications
	},

	/**
     * Fetch engines
     */
	async [ACTION_TYPES.FETCH_ENGINES] ({ commit }, id) {
		const data = await executeSimpleQuery({
			query: FetchEngines,
			variables: {
				payload: { modificationId: id },
				size: 9000
			}
		})

		const engines = data.engines.nodes
		commit(PART_MUTATIONS.SET_ENGINES, engines)
		return engines
	},

	/**
     * Fetch suspension types
     */
	async [ACTION_TYPES.FETCH_SUSPENSION_TYPES] () {
		const data = await executeSimpleQuery({
			query: FetchSuspensionTypes
		})
		return data.suspensionTypes
	},

	/**
     * ========================================================================
     * WHEEL SPECIFICATIONS
     * ========================================================================
     */

	/**
     * Fetch part wheel specifications
     */
	async [ACTION_TYPES.FETCH_PART_WHEEL_SPECIFICATIONS] ({ commit }, id) {
		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'partWheelSpecifications',
			query: {
				query: FetchPartWheelSpecifications,
				variables: { id }
			},
			onSuccess: (data) => {
				commit(PART_MUTATIONS.SET_PART_WHEEL_SPECIFICATIONS, data.part.wheel)
				return data
			}
		})
	},

	/**
     * Fetch part category kinds (isBattery, isWheel, ...)
     */
	async [ACTION_TYPES.FETCH_PART_CATEGORY_KINDS] (_, categoryId) {
		const data = await executeSimpleQuery({
			query: FetchPartCategoryKinds,
			variables: { categoryId }
		})
		return data.partCategoryKinds
	},

	/**
     * Fetch wheel specifications
     */
	async [ACTION_TYPES.FETCH_WHEEL_SPECIFICATIONS] ({ commit }) {
		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'wheelSpecifications',
			query: {
				query: FetchWheelSpecifications,
				fetchPolicy: 'cache-first'
			},
			onSuccess: (data) => {
				commit(PART_MUTATIONS.SET_WHEEL_SPECIFICATIONS, data)
				return data
			}
		})
	},

	/**
     * Edit wheel
     */
	async [ACTION_TYPES.EDIT_WHEEL] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: EditWheel,
				variables: { payload }
			},
			onSuccess: (data) => data
		})
	},

	/**
     * ========================================================================
     * PARTS LISTING (Main queries with complex payload building)
     * ========================================================================
     */

	/**
     * Fetch parts meta
     */
	async [ACTION_TYPES.FETCH_PARTS_META] ({ commit, state, rootState }, data = {}) {
		const { payload, page, size } = buildPartsQueryPayload({ state, rootState, data })
		const { nearestParentId } = data

		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'meta',
			query: {
				query: FetchPartsMeta,
				variables: { payload, page, size }
			},
			onSuccess: (data) => {
				if (!nearestParentId) {
					const meta = data.partsMeta
					commit(PART_MUTATIONS.SET_META, meta)
					return meta
				}
				return state.meta
			}
		})
	},

	/**
     * Fetch parts statistics
     */
	async [ACTION_TYPES.FETCH_PARTS_STATISTICS] ({ commit, state, rootState }, data = {}) {
		const { fetchQuantity = false, ..._data } = data
		const { payload, _page, _size } = buildPartsQueryPayload({ state, rootState, data: _data })
		const { nearestParentId } = data

		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'statistics',
			query: {
				query: FetchPartsStatistics,
				variables: { payload, fetchQuantity }
			},
			onSuccess: (data) => {
				if (!nearestParentId) {
					const statistics = data.partStatistics
					commit(PART_MUTATIONS.SET_PARTS_STATISTICS, statistics)
					return statistics
				}
				return state.statistics
			}
		})
	},

	/**
     * Fetch parts (main listing)
     */
	async [ACTION_TYPES.FETCH_PARTS] ({ commit, state, rootState }, data = {}) {
		const { nearestParentId, page, isRootsChild = true } = data
		const { payload, page: queryPage, size } = buildPartsQueryPayload({ state, rootState, data })

		const sort = buildPartsSort(state.Table, rootState, state.showing)
		const columns = buildPartsColumns(state.Table.columns, state.Table.originalColumns)

		// Reset selections on first page
		const clearFilters = [
			!page || parseInt(page) === 1,
			isRootsChild
		]

		if (clearFilters.every(rule => rule)) {
			commit(PART_MUTATIONS.SET_SELECTED_PARTS, {})
			commit(`Table/${TABLE_MUTATIONS.SET_SELECTED}`, {})
		}

		const responseData = await executeSimpleQuery({
			query: FetchParts,
			variables: {
				payload,
				page: queryPage,
				size,
				sort,
				...columns,
				showDemandContext: !!columns.showDemand
			}
		})

		const parts = responseData.parts.nodes.map(part => {
			const { vehicleSpecifications, ...restPart } = part
			const nearestChildren = processNearestChildren(part)
			return new Part({
				...vehicleSpecifications,
				...restPart,
				nearestChildren
			})
		})

		if (!nearestParentId) {
			const meta = Object.keys(state.meta).length
				? state.meta
				: {
					currentPage: 1,
					nextPage: null,
					pages: 1,
					previousPage: null,
					size: null,
					total: 0
				}

			commit(`Table/${TABLE_MUTATIONS.SET_DATA}`, parts)
			commit(PART_MUTATIONS.SET_PARTS, parts)
			commit(PART_MUTATIONS.SET_META, meta)
			commit(PART_MUTATIONS.SET_SEARCH_FILTERS, payload)
		}

		return parts
	},

	/**
     * Fetch related parts
     */
	async [ACTION_TYPES.FETCH_RELATED_PARTS] (
		{ commit, state, rootState },
		{ nearestParentId = null, page, size, alternativeOemCode, modificationFitments, isRootsChild = true, hasPrice = null, isSaleAllowed = null } = {}
	) {
		const { payload, page: queryPage, size: querySize } = buildPartsQueryPayload({
			state,
			rootState,
			data: { nearestParentId, page, size, hasPrice, isSaleAllowed, isRootsChild }
		})

		const sort = buildPartsSort(state.Table, rootState, state.showing)
		const columns = buildPartsColumns(state.Table.columns, state.Table.originalColumns)

		// Reset selections on first page
		const clearFilters = [
			!page || parseInt(page) === 1,
			isRootsChild
		]

		if (clearFilters.every(rule => rule)) {
			commit(PART_MUTATIONS.SET_SELECTED_PARTS, {})
			commit(`Table/${TABLE_MUTATIONS.SET_SELECTED}`, {})
		}

		// Clean payload for related parts query
		const cleanedPayload = cleanRelatedPartsPayload(payload, {
			alternativeOemCode,
			modificationFitments
		})

		const data = await executeSimpleQuery({
			query: FetchRelatedParts,
			variables: {
				payload: cleanedPayload,
				page: queryPage,
				size: querySize,
				sort,
				...columns
			}
		})

		const parts = data.parts?.nodes.map(part => {
			const { vehicleSpecifications, ...restPart } = part
			const nearestChildren = processNearestChildren(part)
			return new Part({
				...vehicleSpecifications,
				...restPart,
				nearestChildren
			})
		})

		return {
			parts,
			meta: data.partsMeta
		}
	},

	/**
     * Fetch related fitments
     */
	async [ACTION_TYPES.FETCH_RELATED_FITMENTS] (
		_,
		{
			oemCodes,
			categoryId,
			modificationId,
			manufacturerId,
			showOemFitments = true,
			showVehicleFitments = true,
			showPartCategoryFitments = false
		}
	) {
		const data = await executeSimpleQuery({
			query: FetchRelatedFitments,
			variables: {
				payload: {
					oemCodes,
					categoryId,
					modificationId,
					manufacturerId
				},
				showOemFitments,
				showPartCategoryFitments,
				showVehicleFitments
			}
		})
		return data
	},

	/**
     * Export parts
     */
	async [ACTION_TYPES.EXPORT_PARTS] ({ rootState, state }, _data) {
		const { payload } = buildPartsQueryPayload({
			state,
			rootState,
			data: _data.filters
		})

		return executeMutation({
			mutation: {
				mutation: ExportParts,
				variables: {
					payload: {
						filters: payload,
						columns: _data.columns
					}
				}
			}
		})
	},

	/**
     * ========================================================================
     * SINGLE PART OPERATIONS
     * ========================================================================
     */

	/**
     * Fetch single part
     */
	async [ACTION_TYPES.FETCH_PART] ({ commit }, id) {
		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'part',
			query: {
				query: FetchPart,
				variables: { id }
			},
			onSuccess: (data) => {
				const part = new Part(data.part)
				commit(PART_MUTATIONS.SET_CURRENT_PART, part)
				return part
			}
		})
	},

	/**
     * Fetch part tasks
     */
	async [ACTION_TYPES.FETCH_PART_TASKS] ({ commit, state }, id) {
		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'tasks',
			query: {
				query: FetchPartTasks,
				variables: { id }
			},
			onSuccess: (data) => {
				const part = new Part({
					...state.currentPart,
					status: data.part.status,
					location: data.part.location,
					suggestedLocation: data.part.suggestedLocation,
					tasks: data.part.tasks,
					children: state.currentPart.children.map((child, index) => ({
						...child,
						...data.part.children[index]
					}))
				})
				commit(PART_MUTATIONS.SET_CURRENT_PART, part)
				return part
			}
		})
	},

	/**
     * Fetch part logs
     */
	async [ACTION_TYPES.FETCH_PART_LOGS] ({ commit, state }, id) {
		const _id = id || state.currentPart.id

		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'logs',
			query: {
				query: FetchPartLogs,
				fetchPolicy: 'no-cache',
				variables: {
					id: _id,
					size: 50
				}
			},
			onSuccess: (data) => {
				commit(PART_MUTATIONS.SET_LOGS, data.partLogs.nodes)
				return data.partLogs.nodes
			}
		})
	},

	/**
     * Fetch external items
     */
	async [ACTION_TYPES.FETCH_EXTERNAL_ITEMS] ({ commit, state }, id) {
		const data = await executeSimpleQuery({
			query: FetchExternalItems,
			variables: {
				id,
				partId: id,
				payload: { partId: id }
			}
		})

		const part = new Part({
			...state.currentPart,
			...data,
			ebayItems: data.part.ebayItems,
			dalysltPart: data.part.dalysltPart,
			kumasoftPart: data.part.kumasoftPart,
			claimPartsPart: data.part.claimPartsPart,
			proxyPartsPart: data.proxyPartsPart,
			bildelsbasenPart: data.part.bildelsbasenPart,
			rrrPart: data.rrrPart,
			allegroPart: data.allegroPart || [],
			customIntegrations: data.part.customExternalIntegrations
		})

		commit(PART_MUTATIONS.SET_CURRENT_PART, part)
		return part
	},

	/**
     * ========================================================================
     * PART MUTATIONS (Create, Edit, Delete)
     * ========================================================================
     */

	/**
     * Create part
     */
	async [ACTION_TYPES.CREATE_PART] ({ commit, state }, payload) {
		const data = await executeMutation({
			mutation: {
				mutation: CreatePart,
				variables: { payload }
			},
			onSuccess: (data) => data.createPart
		})

		if (state.parts) {
			const newParts = data.map(part => ({
				...part,
				newPart: true
			}))
			let parts = [...state.parts]
			const meta = { ...state.meta }

			commit(PART_MUTATIONS.SET_META, {
				...meta,
				total: meta.total + newParts.length,
				pages: meta.size - (meta.total + newParts.length) > 0 ? meta.pages : meta.pages + 1
			})

			if (meta.size && parts.length + newParts.length > meta.size) {
				const overflow = (parts.length + newParts.length) - meta.size
				parts = parts.slice(0, overflow * -1)
			}

			const formatted = [...newParts, ...parts].map(rec => {
				const { vehicleSpecifications, ...restPart } = rec
				return { ...vehicleSpecifications, ...restPart }
			})

			const preparedParts = formatted.map(part => new Part(part))
			commit(`Table/${TABLE_MUTATIONS.SET_DATA}`, preparedParts)
			commit(PART_MUTATIONS.SET_PARTS, preparedParts)
			return preparedParts
		}

		return data
	},

	/**
     * Edit part
     */
	async [ACTION_TYPES.EDIT_PART] ({ state, commit, dispatch }, payload) {
		return executeMutation({
			mutation: {
				mutation: EditPartMutation,
				variables: { payload }
			},
			onSuccess: (data) => {

				if (state.parts) {
					const updatedParts = state.parts.map(_part => {
						if (data.editPart.id === _part.id) {
							return {
								..._part,
								...data.editPart,
								pictures: _part.pictures
							}
						}
						return _part
					})

					const preparedParts = updatedParts.map(part => new Part(part))
					commit(PART_MUTATIONS.SET_PARTS, preparedParts)
				}

				const partMatch = state.currentPart.id === data.editPart.id  ? state.currentPart : {}

				const updatedPart = new Part({
					...partMatch,
					...data.editPart,
					department: {
						...partMatch?.department,
						...data.editPart.department
					}
				})

				dispatch(ACTION_TYPES.FETCH_PART_LOGS, updatedPart.id)
				dispatch(ACTION_TYPES.FETCH_PART_TASKS, updatedPart.id)
				commit(PART_MUTATIONS.SET_CURRENT_PART, updatedPart)

				return updatedPart
			}
		})
	},

	/**
     * Handover order parts
     */
	async [ACTION_TYPES.HANDOVER_ORDER_PARTS] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: HandoverOrderParts,
				variables: { payload }
			},
			onSuccess: (data) => data.handoverOrderParts.status
		})
	},

	/**
     * Edit part price
     */

	async [ACTION_TYPES.EDIT_PART_PRICE] ({ state, commit, dispatch }, payload) {
		return executeMutation({
			mutation: {
				mutation: EditPartMutation,
				variables: { payload }
			},
			onSuccess: (data) => {
				let part = data.editPart

				// Clone the current part to avoid direct mutation
				// We use a shallow clone here, but for deep nested properties like children,
				// we need to be careful. Since we are replacing the specific child object,
				// a map operation on children is the safest immutable way.
				let updatedCurrentPart = { ...state.currentPart }

				if (state.currentPart.id === payload.id) {
					dispatch(ACTION_TYPES.FETCH_PART_LOGS, part.id)
					updatedCurrentPart.price = part.price
				} else {
					// Immutable update of the children array
					if (updatedCurrentPart.children) {
						updatedCurrentPart.children = updatedCurrentPart.children.map(child => {
							if (child.id === part.id) {
								// Return a new object for the updated child
								return { ...child, price: part.price }
							}
							return child
						})
					}
				}

				// Commit the new state via mutation
				commit(PART_MUTATIONS.SET_CURRENT_PART, new Part(updatedCurrentPart))

				return new Part(part)
			}
		})
	},


	/**
     * Delete single part
     */
	async [ACTION_TYPES.DELETE_PART] (_, part) {
		return executeMutation({
			mutation: {
				mutation: DeletePart,
				variables: {
					payload: { parts: [part] }
				}
			},
			onSuccess: (data) => data
		})
	},

	/**
     * Delete parts (from current part children)
     */
	async [ACTION_TYPES.DELETE_PARTS] ({ getters, commit }, parts) {
		const data = await executeMutation({
			mutation: {
				mutation: DeletePartsMutation,
				variables: {
					payload: { parts }
				}
			},
			onSuccess: (data) => data.deleteParts
		})

		const ids = parts.map(part => part.id)
		const updatedPartChildren = getters.currentPart.children.filter(
			child => !ids.includes(child.id)
		)

		commit(PART_MUTATIONS.SET_CURRENT_PART, new Part({
			...getters.currentPart,
			children: updatedPartChildren
		}))

		return data
	},

	/**
     * Remove parts (general deletion)
     */
	async [ACTION_TYPES.REMOVE_PARTS] (_, parts) {
		return executeMutation({
			mutation: {
				mutation: DeletePartsMutation,
				variables: {
					payload: { parts }
				}
			},
			onSuccess: (data) => data
		})
	},

	/**
     * Restore part
     */
	async [ACTION_TYPES.RESTORE_PART] ({ commit, dispatch }, id) {
		return executeQueryWithLoading({
			commit,
			mutationName: PART_MUTATIONS.SET_LOADING,
			loadingKey: 'part',
			query: {
				mutation: RestorePartMutation,
				variables: { id }
			},
			onSuccess: (data) => {
				dispatch(ACTION_TYPES.FETCH_PART, id)
				return data
			}
		})
	},

	/**
     * Detach parts
     */
	async [ACTION_TYPES.DETACH_PARTS] (_, ids) {
		return executeMutation({
			mutation: {
				mutation: DetachParts,
				variables: {
					payload: { ids }
				}
			},
			onSuccess: (data) => data
		})
	},

	/**
     * Bulk update parts price
     */
	async [ACTION_TYPES.BULK_UPDATE_PARTS_PRICE] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: BulkUpdatePartsPrice,
				variables: { payload }
			},
			onSuccess: (data) => data
		})
	},

	/**
     * Bulk update parts location
     */
	async [ACTION_TYPES.BULK_UPDATE_PARTS_LOCATION] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: BulkUpdatePartsLocation,
				variables: { payload }
			},
			onSuccess: (data) => data
		})
	},

	/**
     * ========================================================================
     * PART CATEGORIES & SEARCH
     * ========================================================================
     */

	/**
     * Fetch part categories
     */
	async [ACTION_TYPES.FETCH_PART_CATEGORIES] (_, payload) {
		const data = await executeSimpleQuery({
			query: FetchPartCategories,
			variables: {
				payload: payload || {},
				size: 20
			}
		})
		return data.partCategories?.nodes
	},

	/**
     * Fetch category set
     */
	async [ACTION_TYPES.FETCH_CATEGORY_SET] (_, id) {
		const data = await executeSimpleQuery({
			query: FetchCategorySet,
			variables: { id }
		})
		return createPartCategorySet(data.categorySet)
	},

	/**
     * Search part categories
     */
	async [ACTION_TYPES.SEARCH_PART_CATEGORIES] (
		_,
		{ searchPhrase, isRoot = null, categoryType = null } = {}
	) {
		const query = {
			query: SearchPartCategoriesQuery,
			variables: {
				payload: {
					searchPhrase,
					isRoot
				},
				size: 20
			}
		}

		if (categoryType) {
			query.variables.payload.categoryType = categoryType
		}

		const data = await executeSimpleQuery(query)
		return data.categorySetSearchSuggestions.nodes
	},

	/**
     * Fetch parts search suggestions
     */
	async [ACTION_TYPES.FETCH_PARTS_SEARCH_SUGGESTIONS] ({ state, rootState }, { searchPhrase, departmentIds }) {
		const query = {
			query: FetchPartSearchSuggestions,
			variables: {
				payload: { searchPhrase },
				size: 30
			}
		}

		// Handle partnership logic for search
		if (rootState.Auth.user.hasPartnership) {
			const partnerships = rootState.Auth.user.partnerships
			let departmentIdsList = partnerships.map(role => role.departmentId)
			const selectedDepartmentId = rootState.Auth.user.selectedDepartmentId
			if (selectedDepartmentId) {
				departmentIdsList = [...departmentIdsList, selectedDepartmentId]
			}
			query.variables.payload.departmentIds = [...new Set(departmentIdsList)]
		} else if (rootState.Auth.user.selectedDepartmentId) {
			query.variables.payload.departmentIds = [rootState.Auth.user.selectedDepartmentId]
		}

		// Override if departmentIds provided
		if (departmentIds) {
			query.variables.payload.departmentIds = departmentIds
		}

		// Use search filters if available
		if (Object.keys(state.searchFilters).length && state.searchFilters.departmentIds) {
			query.variables.payload.departmentIds = state.searchFilters.departmentIds
		}

		const data = await executeSimpleQuery(query)
		return data.partSearchSuggestions?.nodes ?? []
	},

	/**
     * Fetch part category search suggestions
     */
	async [ACTION_TYPES.FETCH_PART_CATEGORY_SEARCH_SUGGESTIONS] (_, payload) {
		const data = await executeSimpleQuery({
			query: FetchPartCategorySearchSuggestions,
			variables: {
				payload,
				size: 20
			}
		})
		return data.partCategorySearchSuggestions?.nodes ?? []
	},

	/**
     * Fetch input part search suggestions
     */
	async [ACTION_TYPES.FETCH_INPUT_PART_SEARCH_SUGGESTIONS] (_, searchPhrase) {
		const data = await executeSimpleQuery({
			query: FetchInputPartSearchSuggestion,
			variables: {
				payload: { searchPhrase },
				size: 20
			}
		})
		return data.inputPartSearchSuggestions?.nodes ?? []
	},

	async [ACTION_TYPES.FETCH_PARTS_DRAFTS] (__, payload) {


		const { page, size, ..._payload } = payload
		const data = await executeSimpleQuery({
			query: FetchPartsDrafts,
			variables: {
				payload: _payload,
				size,
				page
			}
		})
		return {
			nodes: data.parts.nodes?.map(part => new Part(part)) || []
		}

	},


	async [ACTION_TYPES.FETCH_PARTS_DRAFTS_META] (__, payload) {
		const { page, size, ..._payload } = payload
		const data = await executeSimpleQuery({
			query: FetchPartsDraftsMeta,
			variables: {
				payload: _payload,
				size,
				page
			}
		})
		return {
			meta: data.partsMeta
		}
	},

	async [ACTION_TYPES.GET_VEHICLE_FROM_PLATE_NUMBER] (_, payload) {
		const data = await executeSimpleQuery({
			query: GetVehicleFromPlateNumber,
			variables: {
				plateNumber: payload.plateNumber,
				departmentId: payload.departmentId
			}
		})

		const vehicle = data.getVehicleFromPlateNumber
		return vehicle
	}

}
